import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

export default function ResetPassword() {
    // The reset email links here as /reset-password?token=...&email=...
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    // No token or email in the URL means the link is broken/incomplete.
    const linkIsValid = Boolean(token && email);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        if (password !== passwordConfirmation) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            await api.post('/reset-password', {
                email,
                token,
                password,
                password_confirmation: passwordConfirmation,
            });
            setSuccess(true);
        } catch (err) {
            // Validation failures arrive as the 422 error envelope:
            //   { success: false, error: "...", errors: { field: [messages] } }
            // An invalid/expired token comes back as an error on the `email`
            // field — but there's no email input on this page (it comes from
            // the URL), so we surface that in the general error banner.
            const data = err.response?.data;
            const errors = data?.errors || {};
            setFieldErrors(errors);
            if (errors.email || errors.token) {
                setError(
                    'This reset link is invalid or has expired. Please request a new one.'
                );
            } else if (!errors.password) {
                setError(
                    data?.error || 'Something went wrong. Please try again.'
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">🎁 Wishful</h1>
                    <p className="text-gray-600 mt-2">Choose a new password</p>
                </div>

                {!linkIsValid ? (
                    <div>
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                            This password reset link is incomplete. Please use
                            the link from your email, or request a new one.
                        </div>
                        <Link
                            to="/forgot-password"
                            className="block w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 font-medium text-center"
                        >
                            Request a New Link
                        </Link>
                    </div>
                ) : success ? (
                    <div>
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
                            Your password has been reset. You can now sign in
                            with your new password.
                        </div>
                        <Link
                            to={`/login?email=${encodeURIComponent(email)}`}
                            className="block w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 font-medium text-center"
                        >
                            Sign In
                        </Link>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                                {error}{' '}
                                {(fieldErrors.email || fieldErrors.token) && (
                                    <Link
                                        to="/forgot-password"
                                        className="text-red-800 underline font-medium"
                                    >
                                        Request a new link
                                    </Link>
                                )}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <p className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600 text-sm">
                                    {email}
                                </p>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                    New Password
                                </label>
                                <input
                                    id="password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="••••••••"
                                    minLength="8"
                                />
                                {fieldErrors.password && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {fieldErrors.password[0]}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-1">
                                    Confirm New Password
                                </label>
                                <input
                                    id="password_confirmation"
                                    type="password"
                                    required
                                    value={passwordConfirmation}
                                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="••••••••"
                                    minLength="8"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    </>
                )}

                <p className="mt-6 text-center text-sm text-gray-600">
                    Remembered your password?{' '}
                    <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
